import {
  Alert,
  Box,
  ContentLayout,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { push } from 'connected-react-router';
import OrbitPageGate from './components/OrbitPageGate';
import { ROUTE_PATH, getRoutePathWithStudyPeriod } from '../../common/constants/route_paths';
import { getStudyPeriod } from '../../common/constants/study_period';
import { listComplianceSeasonsActions } from './redux/listComplianceSeasons';
import { jurisdictionLabel } from './utils/jurisdiction';
import { IN_PROGRESS_STATES } from './utils/seasonState';

const PAGE_SIZE = 10;

const seasonStatusIndicator = season => {
  const state = String(get(season, 'state', '')).toUpperCase();
  if (state === 'CLOSED') {
    return <StatusIndicator type="success">Closed</StatusIndicator>;
  }
  if (IN_PROGRESS_STATES.includes(state)) {
    return <StatusIndicator type="in-progress">Closing</StatusIndicator>;
  }
  if (state === 'CLOSE_FAILED') {
    return <StatusIndicator type="error">Close failed</StatusIndicator>;
  }
  return <StatusIndicator type="info">Open</StatusIndicator>;
};

const ComplianceSeasonPage = ({
  seasons,
  isFetching,
  error,
  fetchSeasons,
  navigateTo,
  currentStudyPeriod,
}) => {
  const [filteringText, setFilteringText] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  const filteredSeasons = seasons.filter(item => {
    if (!filteringText) return true;
    const q = filteringText.toLowerCase();
    return (
      String(get(item, 'studyPeriod', ''))
        .toLowerCase()
        .includes(q) ||
      String(get(item, 'state', ''))
        .toLowerCase()
        .includes(q)
    );
  });
  const pagesCount = Math.max(1, Math.ceil(filteredSeasons.length / PAGE_SIZE));
  const startIndex = (currentPageIndex - 1) * PAGE_SIZE;
  const paginatedSeasons = filteredSeasons.slice(startIndex, startIndex + PAGE_SIZE);

  const seasonsTable = (
    <Table
      variant="container"
      header={
        <Header variant="h2" counter={`(${filteredSeasons.length})`}>
          Compliance seasons
        </Header>
      }
      loading={isFetching}
      loadingText="Loading compliance seasons"
      empty={
        <Box textAlign="center" color="inherit">
          No compliance seasons found
        </Box>
      }
      items={paginatedSeasons}
      columnDefinitions={[
        {
          id: 'studyPeriod',
          header: 'Compliance season',
          cell: item => {
            // Defensive: fall back to the current route's studyPeriod if the
            // season row lacks a studyPeriod attribute (bad seed data would
            // otherwise produce a year-less URL that hits the not-found redirect).
            const seasonYear =
              String(get(item, 'studyPeriod', '') || '').trim() || currentStudyPeriod;
            const label = jurisdictionLabel(get(item, 'country'));
            const path = getRoutePathWithStudyPeriod(ROUTE_PATH.ORBIT_PAGE, seasonYear);
            return (
              <Link
                href={path}
                onFollow={e => {
                  if (e && e.preventDefault) e.preventDefault();
                  navigateTo(path);
                }}
              >
                {`${label} - ${seasonYear}`}
              </Link>
            );
          },
        },
        {
          id: 'totalBCs',
          header: 'Business components',
          cell: item => get(item, 'totalBCs', 0),
        },
        {
          id: 'totalSurveys',
          header: 'Surveys',
          cell: item => get(item, 'totalSurveys', 0),
        },
        {
          id: 'state',
          header: 'Status',
          cell: item => seasonStatusIndicator(item),
        },
      ]}
      filter={
        <TextFilter
          filteringPlaceholder="Search season"
          filteringText={filteringText}
          onChange={event => {
            setFilteringText(event.detail.filteringText);
            setCurrentPageIndex(1);
          }}
        />
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={pagesCount}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
        />
      }
    />
  );

  const content = (
    <ContentLayout
      headerVariant="high-contrast"
      maxContentWidth={1440}
      header={
        <div style={{ padding: '48px 0 24px' }}>
          <SpaceBetween size="xs">
            <Box fontSize="display-l" variant="h1" padding="n">
              <span style={{ color: '#ffffff' }}>
                <span style={{ fontWeight: 700 }}>Amazon Orbit</span>
                <br />
                <span className="orbit-hero-subtitle">
                  Streamline your R&amp;D tax credit study
                </span>
              </span>
            </Box>
            <Box variant="p" margin={{ top: 'xs' }}>
              <span style={{ color: '#ffffff', maxWidth: 800, display: 'block' }}>
                Orbit is Amazon&apos;s platform for managing R&amp;D tax credit compliance seasons —
                from importing employee and cost data, through collecting labor and substantiation
                surveys, to computing qualified research expenses and preparing filings.
              </span>
            </Box>
          </SpaceBetween>
        </div>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header="Could not load compliance seasons">
            Refresh the page to try again.
          </Alert>
        )}
        {seasonsTable}
      </SpaceBetween>
    </ContentLayout>
  );

  return <OrbitPageGate fullBleedContent>{content}</OrbitPageGate>;
};

ComplianceSeasonPage.propTypes = {
  currentStudyPeriod: PropTypes.string.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.object),
  isFetching: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  fetchSeasons: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
};

ComplianceSeasonPage.defaultProps = {
  seasons: [],
  isFetching: false,
  error: null,
};

const mapStateToProps = state => ({
  currentStudyPeriod: getStudyPeriod(state),
  seasons: get(state, 'entities.complianceSeasons.data.seasons', []),
  isFetching: get(state, 'entities.complianceSeasons.isFetching', false),
  error: get(state, 'entities.complianceSeasons.error', null),
});

const mapDispatchToProps = dispatch => ({
  fetchSeasons: () => dispatch(listComplianceSeasonsActions.BEGIN()),
  navigateTo: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ComplianceSeasonPage);
